import { ReactNode, useEffect, useRef, useState } from 'react';

import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious
} from '@/components/ui/carousel';

const CARD_WIDTH = 260;
const GAP = 16;
const PEEK_FRACTION = 0.4;
const DRAG_CLICK_THRESHOLD = 6;

interface ArtifactCarouselProps {
    count: number;
    renderCard: (index: number) => ReactNode;
}

export function ArtifactCarousel({
    count,
    renderCard
}: ArtifactCarouselProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const pointerStartX = useRef(0);
    const [width, setWidth] = useState(0);
    const [api, setApi] = useState<CarouselApi>();
    const [cues, setCues] = useState({ previous: false, next: false });

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const observer = new ResizeObserver(() =>
            setWidth(wrapper.clientWidth)
        );
        observer.observe(wrapper);
        setWidth(wrapper.clientWidth);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!api) return;
        const update = () =>
            setCues({
                previous: api.canScrollPrev(),
                next: api.canScrollNext()
            });
        update();
        api.on('select', update);
        api.on('reInit', update);
        return () => {
            api.off('select', update);
            api.off('reInit', update);
        };
    }, [api]);

    const fitsAll = count * CARD_WIDTH + (count - 1) * GAP <= width;
    const visible = Math.max(1, Math.floor((width + GAP) / (CARD_WIDTH + GAP)));
    const cardWidth = fitsAll
        ? CARD_WIDTH
        : Math.round((width - visible * GAP) / (visible + PEEK_FRACTION));

    return (
        <div ref={wrapperRef} className="relative">
            <Carousel
                setApi={setApi}
                opts={{ align: 'start', containScroll: 'trimSnaps' }}
            >
                <CarouselContent className="-ml-4 cursor-grab active:cursor-grabbing">
                    {Array.from({ length: count }, (_, index) => (
                        <CarouselItem
                            key={index}
                            className="basis-auto"
                            style={{ width: cardWidth + GAP }}
                            onPointerDownCapture={(event) => {
                                pointerStartX.current = event.clientX;
                            }}
                            onClickCapture={(event) => {
                                if (
                                    Math.abs(
                                        event.clientX - pointerStartX.current
                                    ) > DRAG_CLICK_THRESHOLD
                                ) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                            }}
                        >
                            {renderCard(index)}
                        </CarouselItem>
                    ))}
                </CarouselContent>
                {cues.previous && (
                    <div className="top-0 bottom-0 left-0 absolute bg-linear-to-r from-background to-transparent w-14 pointer-events-none" />
                )}
                {cues.next && (
                    <div className="top-0 right-0 bottom-0 absolute bg-linear-to-l from-background to-transparent w-14 pointer-events-none" />
                )}
                <CarouselPrevious className="left-2 bg-background disabled:hidden" />
                <CarouselNext className="right-2 bg-background disabled:hidden" />
            </Carousel>
        </div>
    );
}
